"use client";
import { throttle } from "lodash";
import cn from "classnames";
import styles from "./ContentsTable.module.scss";
import { useEffect, useState, useRef, useCallback, MouseEvent } from "react";
import { animateElementScroll, animateScroll } from "helpers/animateScroll";

export type ContentsTableItem = {
  id: string;
  heading: string;
};

export type ContentsTableProps = {
  items: ContentsTableItem[];
  shrinkHeight?: boolean;
};

export const ContentsTable = ({ items, shrinkHeight = true }: ContentsTableProps) => {
  if (!items.length) return null;
  return <Core items={items} shrinkHeight={shrinkHeight} />;
};

const Core = ({ items, shrinkHeight }: ContentsTableProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [winWidth, setWinWidth] = useState(0);
  const [scrollingMode, setScrollingMode] = useState(true);

  const scroller = useRef<HTMLSpanElement>(null);
  const navContainer = useRef<HTMLDivElement>(null);
  const scrollingModeRef = useRef<boolean>(scrollingMode);
  const activeIndexRef = useRef<number>(activeIndex);

  scrollingModeRef.current = scrollingMode;
  activeIndexRef.current = activeIndex;

  const checkPosition = useCallback(() => {
    let elemFound = false;
    const winTop = window.scrollY;

    if (window.innerHeight + window.scrollY >= document.body.offsetHeight) {
      setActiveIndex(items.length - 1);
      return;
    }

    items.forEach((item, index) => {
      if (elemFound) return;
      const targetElem = document.getElementById(item.id)?.parentElement;
      if (!targetElem) return;

      const elTop = winTop + targetElem.getBoundingClientRect().top;

      if (window.matchMedia("(max-width: 992px)").matches) {
        const targetHeight = winTop + 200;

        if (targetHeight >= elTop && winTop < elTop + targetElem.clientHeight - 200) {
          setActiveIndex(index);

          elemFound = true;
        }
      } else {
        const targetHeight = winTop + 200;
        const topDiff = 140;
        const bottomDiff = 80;

        if (targetHeight >= elTop - topDiff && winTop < elTop + targetElem.clientHeight - bottomDiff) {
          setActiveIndex(index);

          elemFound = true;
        }
      }
    });
  }, [items]);

  // const throttledCheckPosition = throttle(checkPosition, 100);
  const throttledCheckPosition = checkPosition;

  const checkTableScroll = useCallback(() => {
    if (!scrollingModeRef.current || !navContainer.current || !scroller.current) return;
    const targetElem = navContainer.current.querySelector(`[href="#${items[activeIndexRef.current]?.id}"]`);
    if (!targetElem) return;
    const parent = targetElem.parentElement;

    if (!parent) return;

    if (window.matchMedia("(max-width: 992px)").matches) {
      animateElementScroll(
        "x",
        navContainer.current,
        navContainer.current.scrollLeft +
          parent.getBoundingClientRect().left -
          30 -
          (navContainer.current.clientWidth / 2 - parent.clientWidth / 2 - 30),
        () => {
          if (!navContainer.current || !scroller.current) return;
          const leftPosition = navContainer.current.scrollLeft + parent.getBoundingClientRect().left;
          scroller.current.style.width = parent.clientWidth + "px";
          scroller.current.style.transform = `translateX(${leftPosition}px)`;
        }
      );
    } else {
      scroller.current.style.width = "4px";

      animateElementScroll(
        "y",
        navContainer.current,
        navContainer.current.scrollTop +
          (parent.getBoundingClientRect().top - navContainer.current.getBoundingClientRect().top) -
          navContainer.current.clientHeight / 2,
        () => {
          if (!navContainer.current || !scroller.current) return;
          const topPosition =
            navContainer.current.scrollTop +
            parent.getBoundingClientRect().top -
            navContainer.current.getBoundingClientRect().top +
            5;
          scroller.current.style.transform = `translateY(${topPosition}px)`;
        }
      );
    }
  }, [items]);

  useEffect(() => {
    checkTableScroll();
  }, [activeIndex, winWidth, checkTableScroll]);

  const updateWindowWidth = throttle(() => {
    setWinWidth(window.innerWidth);
  }, 200);

  useEffect(() => {
    window.addEventListener("scroll", throttledCheckPosition);
    window.addEventListener("resize", throttledCheckPosition);
    window.addEventListener("resize", updateWindowWidth);
    checkPosition();

    return () => {
      window.removeEventListener("scroll", throttledCheckPosition);
      window.removeEventListener("resize", throttledCheckPosition);
      window.removeEventListener("resize", updateWindowWidth);
    };
  }, [checkPosition, throttledCheckPosition, updateWindowWidth]);

  const onItemClick = (id: string, event?: MouseEvent) => {
    event?.preventDefault();

    const el = document.getElementById(id)?.parentElement;

    if (!el) return;

    const offset = window.matchMedia("(max-width: 992px)").matches ? -60 : 0;

    setScrollingMode(false);

    animateScroll(el, offset, () => {
      setScrollingMode(true);
      checkTableScroll();
    });
  };

  useEffect(() => {
    const hash = (location.hash || "").replace(/#/, "");

    if (!hash) return;

    const targetItem = items.find(({ id }) => id === hash);

    if (!targetItem) return;

    setTimeout(() => {
      const el = document.getElementById(hash)?.parentElement;

      if (!el) return;

      const offset = window.matchMedia("(max-width: 992px)").matches ? -60 : 0;

      const position = el.getBoundingClientRect().top + window.scrollY - 78 + offset;

      window.scrollTo(0, position);
    }, 0);
  }, [items]);

  return (
    <div
      className={cn(styles.contentsTable, {
        [styles.shrinkHeight]: shrinkHeight,
      })}
    >
      <p className={styles.heading}>Table of contents</p>
      <div className={styles.navWrapper}>
        <nav className={styles.nav} ref={navContainer}>
          <span ref={scroller} className={styles.navScroller}></span>
          <ul className={styles.list}>
            {items.map((item, index) => (
              <li
                key={item.id}
                className={cn(styles.item, {
                  [styles.itemActive]: index === activeIndex,
                })}
              >
                <a href={`#${item.id}`} onClick={(event) => onItemClick(item.id, event)}>
                  {item.heading}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </div>
  );
};
